import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on mount — skip for anonymous public visitors (avoids 401 console errors)
  useEffect(() => {
    const mayHaveSession = sessionStorage.getItem('auth_token')
      || localStorage.getItem('reshapers_was_authed') === '1'
      || window.location.pathname.startsWith('/admin');
    if (mayHaveSession) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
      localStorage.setItem('reshapers_was_authed', '1');
    } catch (error) {
      // Clear any stale auth data
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('reshapers_was_authed');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSession = async (sessionId) => {
    try {
      const response = await authAPI.processSession(sessionId);
      
      // Store session token in localStorage as fallback for browsers that block cookies
      // The backend returns the session token in the response for this purpose
      if (response.data?.session_token) {
        sessionStorage.setItem('auth_token', response.data.session_token);
      } else {
      }
      localStorage.setItem('reshapers_was_authed', '1');
      
      setUser(response.data);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      console.error('[AUTH] Failed to process session:', error);
      // Extract error message from response
      const errorMessage = error.response?.data?.detail || 'Authentication failed';
      sessionStorage.removeItem('auth_token');
      setUser(null);
      setIsAuthenticated(false);
      // Throw error with the message so it can be displayed
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('reshapers_was_authed');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    processSession,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
